import { cn } from 'utils/shadcn';
import { Badge } from '../shadcn/ui/badge';
import { Slider } from '../shadcn/ui/slider';
import { parseFloatWithCommas } from '../../utils/currency';
import isNil from 'lodash/isNil';
import { NumberInput } from 'components/shadcn/ui/number-input';

export function SliderInput({
  max,
  min,
  step = 1,
  value,
  onChange,
  unit = '',
  label,
  numTicks = 0,
  helperText,
  inputClassName = '',
  trackClassName = '',
}: {
  max: number;
  min: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  unit?: string;
  label: string;
  numTicks?: number;
  helperText: string;
  inputClassName?: string;
  trackClassName?: string;
}) {
  const id = label.replace(/\s+/g, '-').toLowerCase(); // Generate id from label

  const stepSize = numTicks > 1 ? (max - min) / (numTicks - 1) : max - min;
  const ticks = [...Array(numTicks)].map((_, i) => min + i * stepSize);
  return (
    <section>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm text-foreground font-medium">
          {label}
        </label>
        <Badge variant="outline" className="border-border border flex items-center px-1 py-1">
          <NumberInput
            className={cn(
              'text-xs p-0 w-7 inline-flex text-center h-4 ml-1 border-none bg-card focus-visible:outline-none focus-visible:ring-0 focus-visible:border-none focus-visible:ring-offset-0',
              inputClassName,
            )}
            value={value}
            min={min}
            decimalScale={2}
            max={max}
            onChange={(e) => {
              const v = parseFloatWithCommas(e.target.value);
              if (isNil(v) || Number.isNaN(v)) {
                onChange(min);
              } else {
                const clampedValue = Math.max(min, Math.min(max, v));
                onChange(clampedValue);
              }
            }}
          />{' '}
          <span>{unit}</span>
        </Badge>
      </div>

      <Slider
        className="my-2"
        id={id}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        trackClassName={trackClassName}
      />

      <span
        className="mt-2 flex font-mono w-full items-center justify-between gap-1 px-2.5 text-xs font-medium text-muted-foreground"
        aria-hidden="true"
      >
        {ticks.map((v) => (
          <span key={v} className="flex w-0 flex-col items-center justify-center gap-2">
            <span className={cn('h-1 w-px', Math.abs(v - value) < step / 2 ? 'bg-primary' : 'bg-muted')} />
            <span className={Math.abs(v - value) < step / 2 ? 'text-primary font-semibold' : 'text-muted'}>{v}</span>
          </span>
        ))}
      </span>

      {helperText && <p className="text-xs text-muted pt-2">{helperText}</p>}
    </section>
  );
}
